import React, { useEffect, useRef, useState } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, ChevronDown, Search } from 'lucide-react';
import { aaiApi } from '../../services/aaiApi';
import { useAsync } from '../aai/useAsync';
import type { PartnerClientRow, PartnerOverview } from '../../types/aai';
import { Action, EmptyState, ErrorNote, Loading, PageBody, PageHead, Panel } from '../../components/aa';
import { OwnerProjectsPage } from '../owner/ProjectsPage';

/*
 * Client projects, for a provider Owner: the same Projects tabs the Owner has
 * for their own company, pointed at one client at a time. The dropdown at the
 * top switches which client every tab reads.
 */

const ClientDropdown: React.FC<{
  clients: PartnerClientRow[];
  value: string;
  onChange: (id: string) => void;
}> = ({ clients, value, onChange }) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const ref = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const picked = clients.find((c) => c.id === value);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current?.contains(e.target as Node)) return;
      setOpen(false);
    };
    const onEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onEsc);
    setTimeout(() => inputRef.current?.focus(), 0);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onEsc);
    };
  }, [open]);

  const needle = q.trim().toLowerCase();
  const matches = clients.filter((c) => !needle || c.name.toLowerCase().includes(needle) || (c.contact_email || '').toLowerCase().includes(needle));
  const pick = (id: string) => {
    onChange(id);
    setOpen(false);
    setQ('');
  };

  return (
    <div ref={ref} className="relative inline-block">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        className="inline-flex h-10 w-[300px] max-w-full items-center justify-between gap-3 rounded-act border border-line-strong bg-white px-3.5 text-[13.5px] font-semibold text-ink transition-colors hover:bg-canvas-inset"
      >
        <span className="truncate">{picked?.name || 'Pick a client'}</span>
        <ChevronDown size={15} className="shrink-0 text-ink-soft" />
      </button>
      {open && (
        <div className="absolute right-0 top-full z-[60] mt-1.5 w-full min-w-[260px] overflow-hidden rounded-panel border border-line bg-white shadow-[0_12px_32px_rgba(24,25,29,0.14)]">
          <div className="flex items-center gap-2 border-b border-line-soft px-3 py-2.5">
            <Search size={14} className="shrink-0 text-ink-soft" />
            <input
              ref={inputRef}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search clients"
              aria-label="Search clients"
              className="w-full border-0 bg-transparent text-[13px] text-ink outline-none placeholder:text-ink-soft"
            />
          </div>
          <ul role="listbox" className="m-0 max-h-72 list-none overflow-y-auto py-1">
            {matches.map((c) => {
              let tone = 'text-ink hover:bg-canvas-inset';
              if (c.id === value) tone = 'bg-canvas-inset font-semibold text-ink';
              return (
                <li key={c.id}>
                  <button type="button" role="option" aria-selected={c.id === value} onClick={() => pick(c.id)} className={`flex w-full flex-col items-start px-3.5 py-2 text-left text-[13px] transition-colors ${tone}`}>
                    <span className="truncate">{c.name}</span>
                    {c.contact_email && <span className="truncate text-[11.5px] font-normal text-ink-soft">{c.contact_email}</span>}
                  </button>
                </li>
              );
            })}
            {matches.length === 0 && <li className="px-3.5 py-3 text-[12.5px] text-ink-soft">No client matches "{q}".</li>}
          </ul>
        </div>
      )}
    </div>
  );
};

const ProviderClientProjectsPage: React.FC = () => {
  const [params, setParams] = useSearchParams();
  const overview = useAsync<PartnerOverview>(() => aaiApi.getPartnerOverview(), 'Could not load your clients.', []);
  useEffect(() => () => aaiApi.setAaiCompany(null), []);

  if (!localStorage.getItem('provider_id')) return <Navigate to="/dashboard" replace />;
  if (overview.loading) return <PageBody><Loading label="Loading your clients" /></PageBody>;
  if (overview.error) return <PageBody><ErrorNote message={overview.error} /></PageBody>;

  const clients = overview.data?.clients || [];
  const picked = clients.find((c) => c.id === params.get('client')) || clients[0] || null;
  const pick = (id: string) => setParams((now) => {
    const next = new URLSearchParams(now);
    next.set('client', id);
    return next;
  }, { replace: true });

  if (!picked) {
    return (
      <PageBody>
        <Panel>
          <EmptyState title="No clients yet" reason="Add a client first. Their projects show up here." action={<Action to="/clients">Back to clients</Action>} />
        </Panel>
      </PageBody>
    );
  }

  // Set during render so the tabs' first reads go out already scoped.
  aaiApi.setAaiCompany(picked.id);

  return (
    <PageBody>
      <Action variant="ghost" size="sm" icon={<ArrowLeft size={14} />} to="/clients" className="mb-4">All clients</Action>
      <PageHead align="center" title="Client projects" actions={<ClientDropdown clients={clients} value={picked.id} onChange={pick} />} />
      <OwnerProjectsPage key={picked.id} embedded clientId={picked.id} />
    </PageBody>
  );
};

export default ProviderClientProjectsPage;
